import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <Link to="/login" className="p-2 hover:bg-slate-100 rounded-full transition-colors" data-testid="terms-back-btn">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </Link>
          <FileText className="w-6 h-6 text-blue-600" />
          <h1 className="text-lg font-bold text-slate-900 font-fredoka">Terms of Service</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8" data-testid="terms-of-service-content">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8 space-y-6 text-slate-700 leading-relaxed text-sm sm:text-base">

          <p className="text-slate-500 text-xs">Last updated: April 3, 2026</p>

          <section>
            <h2 className="text-lg font-bold text-slate-900 font-fredoka mb-2">1. Acceptance of Terms</h2>
            <p>
              By accessing or using BrainGym Jr ("the App"), you agree to be bound by these Terms of Service.
              If you are a parent or guardian registering a child under 18, you agree to these terms on behalf of your child.
              If you do not agree, please do not use the App.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 font-fredoka mb-2">2. Description of Service</h2>
            <p>
              BrainGym Jr is an educational application that provides daily brain training quizzes, puzzles,
              and gamified learning experiences for children aged 6–18. The App includes features such as
              daily quizzes, riddles, a virtual coin economy, pet collections, and progress tracking.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 font-fredoka mb-2">3. Account Registration</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>A parent or guardian must provide their information to create a child account.</li>
              <li>You are responsible for maintaining the confidentiality of account credentials.</li>
              <li>You agree to provide accurate information during registration.</li>
              <li>One account per child. Account sharing is not permitted.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 font-fredoka mb-2">4. Subscription & Payments</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>BrainGym Jr offers a <strong>7-day free trial</strong> for new users.</li>
              <li>After the trial, access requires a subscription at <strong>$3.99 per week</strong> for up to 12 weeks, or an enrollment code.</li>
              <li>Payments are processed securely through Stripe.</li>
              <li>Subscriptions renew weekly and can be cancelled at any time from the App.</li>
              <li>Cancellations take effect at the end of the current billing period. <strong>No refunds</strong> are provided for partial weeks.</li>
              <li>The subscription auto-cancels after 12 weekly payments.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 font-fredoka mb-2">5. Virtual Currency & Items</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>Coins, pets, collectibles, and other virtual items earned in the App have no real-world monetary value.</li>
              <li>Virtual items cannot be transferred, sold, or exchanged for real currency.</li>
              <li>We reserve the right to modify the virtual economy at any time.</li>
              <li>"Real Prize Milestones" are discretionary rewards fulfilled manually by BrainGym Jr.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 font-fredoka mb-2">6. Acceptable Use</h2>
            <p>Users agree not to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Attempt to exploit, hack, or manipulate the App or its systems.</li>
              <li>Create multiple accounts to abuse the free trial.</li>
              <li>Use the App for any purpose other than personal educational use.</li>
              <li>Share account credentials with others.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 font-fredoka mb-2">7. Intellectual Property</h2>
            <p>
              All content in BrainGym Jr, including quizzes, designs, logos, and educational materials,
              is the property of BrainGym Jr and is protected by intellectual property laws.
              You may not reproduce, distribute, or create derivative works without our written consent.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 font-fredoka mb-2">8. Account Termination</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>You may delete your account at any time through the Account Settings page.</li>
              <li>We may suspend or terminate accounts that violate these terms.</li>
              <li>Upon termination, all user data will be deleted in accordance with our Privacy Policy.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 font-fredoka mb-2">9. Disclaimer of Warranties</h2>
            <p>
              The App is provided "as is" without warranties of any kind. We do not guarantee that the App
              will be error-free, uninterrupted, or that educational outcomes will be achieved.
              AI-generated content may occasionally contain errors.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 font-fredoka mb-2">10. Limitation of Liability</h2>
            <p>
              To the fullest extent permitted by law, BrainGym Jr shall not be liable for any indirect,
              incidental, or consequential damages arising from the use of the App.
              Our total liability shall not exceed the amount paid by you in the preceding 12 weeks.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 font-fredoka mb-2">11. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. Material changes will be communicated
              via the App or by email to the registered parent email. Continued use after changes
              constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 font-fredoka mb-2">12. Contact</h2>
            <p>
              For questions about these Terms of Service, please contact us at:
            </p>
            <p className="mt-2 font-semibold">
              BrainGym Jr<br />
              Email: write@braingymjr.ca
            </p>
          </section>

        </div>
      </main>
    </div>
  );
}
